import { Ability } from '@casl/ability'
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useRef,
  useState,
} from 'react'
import login, { loginForInvite, updateLogin } from '../../api/login'
import confirmTokenForSignup from '../../api/signup'
import logout from '../../api/logout'
import {
  FetchSessionResponse,
  LoginForInviteRequestBodySignUpTypeEnum,
  Permission,
  RegionDiscourseUrl,
  SignUpConfirmTokenRequestBody,
} from '../../swagger'
import { LoginResponseFeatureDefaults } from '../../swagger'
import { useLearningCenterContext } from '../Context/LearningCenterContext'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { useBusinessContext } from '../Context/BusinessContext'
import { useCommunitiesContext } from '../Context/CommunitiesContext'
import { useFilterContext } from '../Context/FilterContext'
import { auth } from '@api/swagger'
import { useTranslation } from 'react-i18next'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import { AuthSkeleton } from '../Skeleton/AuthSkeleton'
import LoginSkeleton from '../Skeleton/LoginSkeleton'
import { useMountEffect } from '@hooks/useMountEffect'
import { AccountTabs } from '../Account/Account'
import { ACTING_AS_PARENT } from '../../utils/constants'

export const buildPermissionAbility = (permissions: Permission[]): Ability => {
  const rules = permissions.map(
    ({ resourceCode: subject, actionCode: action }) => ({ subject, action })
  )
  const ability = new Ability(rules)
  return ability
}

export const buildFeatureAbility = (
  loadFeatureDefaults: LoginResponseFeatureDefaults[]
): Ability => {
  const rules = loadFeatureDefaults.map(
    ({ resourceCode: subject, actionCode: action }) => ({ subject, action })
  )
  const ability = new Ability(rules)
  return ability
}

const dashboardPath = '/account/dashboard'
const profilePath = '/account/profile'
const billingPath = '/account/billing'

export const hasDashboardPermission = (
  permissionsAbility: Ability,
  featureDefaultsAbility: Ability
): boolean =>
  (featureDefaultsAbility.can('viewDashboard', 'Feature') &&
    permissionsAbility.can('viewDirectorDashboard', 'User')) ||
  featureDefaultsAbility.can('teamLeaderDashboard', 'Feature')

/** Login-time landing rule. Parents go to Billing on first
 * login roles go to Profile. Dashboard-eligible users go to
 * Dashboard regardless of actingAs. */
export const getLandingPath = (
  canViewDashboard: boolean,
  actingAs: number | 'parent'
): string => {
  if (canViewDashboard) return dashboardPath
  return actingAs === 'parent' ? billingPath : profilePath
}

export const getLandingTab = (
  canViewDashboard: boolean,
  actingAs: number | 'parent'
): AccountTabs => {
  if (canViewDashboard) return AccountTabs.Dashboard
  return actingAs === 'parent' ? AccountTabs.Billing : AccountTabs.Profile
}

export const getActorChangeLandingPath = (canViewDashboard: boolean): string =>
  canViewDashboard ? dashboardPath : profilePath

export const getActorChangeLandingTab = (
  canViewDashboard: boolean
): AccountTabs =>
  canViewDashboard ? AccountTabs.Dashboard : AccountTabs.Profile

/** The backend does not actually define this permission - it instead explicitly grants all defined permissions to admin - but Casl understands it as a blank check ability grant, and that list of permissions can change over time. This is convenient as a default when testing, because it matches the pre-permissions app behavior. */
const allowAllActionsOnAllResources: Permission = {
  actionCode: 'manage',
  resourceCode: 'all',
}
const adminPermissions = [allowAllActionsOnAllResources]
const adminPermissionAbility = buildPermissionAbility(adminPermissions)
const adminFeatureAbility = buildFeatureAbility([])

const defaultUserDetails: UserDetails = { actingAs: 'parent' }
const discourseUrl: RegionDiscourseUrl[] = [
  { discourseUrl: '', regionName: '' },
]
const defaultCountryInformation: CountryInformation = {
  countryOfCitizenship: '',
  countryOfResidence: '',
}

/** Provides default values for all info provided by `AuthContext`.
 *
 * Currently, all related types are derived from this exemplar, but if any properties were optional and null here, we would want to introduce an `interface AuthContextValue` to be able to specify the type when the property is not null. */
const defaultAuthContext = {
  isLoggedIn: false,
  permissions: adminPermissions,
  userDetails: defaultUserDetails,
  permissionAbility: adminPermissionAbility,
  featureAbility: adminFeatureAbility,
  discourseUrl: discourseUrl,
  countryInformation: defaultCountryInformation,
  username: '',

  signin: async (_credentials: {
    email: string
    password: string
  }): Promise<SigninResult | undefined> => {
    console.error(
      'XXX: Running the default signin. Did you forget to provide an AuthContext?'
    )
    return new Promise((resolve, _) => {
      setTimeout(() => resolve(undefined), 0)
    })
  },

  updateActingAs: async (actorKey?: number): Promise<UpdateActingAsResult> => {
    console.error(
      `XXX: Runing the default updateActingAs ${actorKey}. Did you forget to provide an AuthContext?`
    )
    return new Promise((resolve, _) => {
      setTimeout(
        () =>
          resolve({ success: true, actingAs: actorKey ?? ACTING_AS_PARENT }),
        0
      )
    })
  },

  signup: async (_payload: SignUpConfirmTokenRequestBody): Promise<boolean> => {
    console.error(
      'XXX: Running the default signup. Did you forget to provide an AuthContext?'
    )
    return new Promise((resolve, _) => {
      setTimeout(() => resolve(true), 0)
    })
  },

  signout: () => {
    console.error(
      'XXX: Running the default signout. Did you forget to provide an AuthContext?'
    )
    return new Promise<void>((resolve, _) => {
      setTimeout(() => resolve(), 0)
    })
  },

  signinForInvite: async (
    _credentials: {
      username: string
      password: string
    },
    _signUpType: LoginForInviteRequestBodySignUpTypeEnum,
    _typeIdentifier: string
  ): Promise<boolean> => {
    console.error(
      'XXX: Running the default signinForInvite. Did you forget to provide an AuthContext?'
    )
    return new Promise((resolve, _) => {
      setTimeout(() => resolve(true), 0)
    })
  },
  updateCountryInformation: (countryInformation: CountryInformation): void => {
    console.error(
      `XXX: Running the default country information ${countryInformation}. Did you forget to provide an AuthContext?`
    )
  },
  revalidateSession: async (): Promise<FetchSessionResponse | undefined> => {
    console.error(
      `XXX: Running the default revalidate. Did you forget to provide an AuthContext?`
    )
    return undefined
  },
  /** True after the initial GET /session has completed (success or 401).
   * Used to gate route rendering so PrivateRoute doesn't redirect to /login
   * before the session check resolves. */
  isInitialized: false as boolean,
  /** Boolean determination of whether the user has the feature ability of viewDashboard */
  canViewDashboard: false as boolean,
  /** Path to navigate for logging in or redirecting when not on a known route for Account */
  defaultAccountPath: '/account/billing' as string,
}

export const AuthContext = createContext(defaultAuthContext)
export const useAuth = (): typeof defaultAuthContext => {
  return useContext(AuthContext)
}

export type UserDetails = {
  userKey?: number
  actingAs: number | 'parent'
}

export type CountryInformation = {
  countryOfCitizenship: string
  countryOfResidence: string
}
export type SigninResult = {
  session: FetchSessionResponse
  landingPath: string
}
export type UpdateActingAsResult = {
  success: boolean
  actingAs: number | typeof ACTING_AS_PARENT
}
export type TestAuthConfig = Partial<typeof defaultAuthContext>

export interface TestAuthConfig1 {
  loadLoggedIn: () => boolean
  loadPermissions: () => Permission[]
  loadFeatureDefaults: () => LoginResponseFeatureDefaults[]
  loadUserDetails: () => UserDetails
  loadDiscourseUrl: () => RegionDiscourseUrl[]
  countryInformation: () => CountryInformation
  /** Registers a handler to reload logged-in and permissions. Should be called when they change. Returns a function to unregister the listener. */
  buildChangeListener?: (handler: () => void) => () => void
  loadRealUserDetails: boolean
}

/** `testConfig` is provided to enable controlling the state seen by the context. signin/signout still continue to work with localStorage, as customizing them was not needed to isolate the core auth context data from _reading_ shared state. */
function useAuthContextValue(
  testConfig?: TestAuthConfig,
  testConfig1?: TestAuthConfig1,
  /** Resets the context defaults for the learning center. */
  resetLearningCenterContextToDefaults?: () => void,
  /** Resets the context defaults for business center */
  resetBusinessContextToDefaults?: () => void,
  /** Resets the context defaults for communities */
  resetCommunitiesContextToDefaults?: () => void,
  /** Resets the context defaults for filter */
  resetFiltersContextToDefaults?: () => void
): typeof defaultAuthContext {
  const loadLoggedIn = testConfig ? testConfig1?.loadLoggedIn : false
  const loadPermissions = testConfig1 ? testConfig1?.loadPermissions : []
  const loadFeatureDefaults = testConfig1
    ? testConfig1?.loadFeatureDefaults
    : []
  const loadUserDetails =
    testConfig1 && !testConfig1?.loadRealUserDetails
      ? testConfig1?.loadUserDetails
      : () => ({ userKey: 0, actingAs: 'parent' } as UserDetails)
  const loadDiscourseUrl = testConfig1 ? testConfig1?.loadDiscourseUrl : []
  const loadCountryInformation = testConfig1
    ? testConfig1?.countryInformation
    : { countryOfResidence: '', countryOfCitizenship: '' }

  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const [isLoggedIn, setIsLoggedIn] = useState(loadLoggedIn)
  const [permissions, setPermissions] = useState(loadPermissions)
  const [defaults, setDefaults] = useState(loadFeatureDefaults)
  const [userDetails, setUserDetails] = useState(loadUserDetails)
  const [discourseUrl, setDiscourseUrl] = useState(loadDiscourseUrl)
  const [countryInformation, setCountryInformation] = useState(
    loadCountryInformation
  )
  const [username, setUsername] = useState<string>('')

  const [permissionAbility, setPermissionAbility] = useState(
    buildPermissionAbility(permissions)
  )
  const [featureAbility, setFeatureAbility] = useState(
    buildFeatureAbility(defaults)
  )
  /* isInitialized starts true so tests that dont pass testConfig
     still exercise the logged in / logged out branching. */
  const [isInitialized, setIsInitialized] = useState(
    testConfig || testConfig1 ? true : false
  )

  /* Shares one promise across concurrent fetchSession calls (mountEffect
     firing while signin is also calling it). Without this, the mountRffect's
     stale 401 can resolve after signin's 200 and reset auth state, logging
     the user out mid way login. */
  const fetchSessionInFlight = useRef<Promise<
    FetchSessionResponse | undefined
  > | null>(null)

  const initialCanViewDashboard = hasDashboardPermission(
    permissionAbility,
    featureAbility
  )
  const [canViewDashboard, setCanViewDashboard] = useState(
    initialCanViewDashboard
  )

  const [defaultAccountPath, setDefaultAccountPath] = useState(
    getLandingPath(initialCanViewDashboard, loadUserDetails().actingAs)
  )

  const {
    AuthProvider: { logout: logoutLoadingId },
  } = useLoadingIds()

  const fetchSession = async (): Promise<FetchSessionResponse | undefined> => {
    if (fetchSessionInFlight.current) {
      return fetchSessionInFlight.current
    }
    const promise = (async (): Promise<FetchSessionResponse | undefined> => {
      try {
        const sessionData = await auth.fetchSession()
        const permissions = sessionData.permissions ?? []
        const permissionsAbility = buildPermissionAbility(permissions)
        const featureDefaults = sessionData.featureDefaults ?? []
        const featureDefaultsAbility = buildFeatureAbility(featureDefaults)

        const userHasDashboardPermission = hasDashboardPermission(
          permissionsAbility,
          featureDefaultsAbility
        )
        const sessionActingAs = sessionData.actingAs ?? 'parent'

        setPermissions(permissions)
        setDefaults(featureDefaults)
        setUserDetails({
          userKey: sessionData.userKey ?? 0,
          actingAs: sessionActingAs,
        })
        setUsername(sessionData.username ?? '')
        setDiscourseUrl(sessionData.discourseUrls ?? [])
        setCountryInformation({
          countryOfResidence: sessionData.countryOfResidence ?? '',
          countryOfCitizenship: sessionData.countryOfCitizenship ?? '',
        })
        setFeatureAbility(featureDefaultsAbility)
        setPermissionAbility(permissionsAbility)
        setCanViewDashboard(userHasDashboardPermission)

        setDefaultAccountPath(
          getLandingPath(userHasDashboardPermission, sessionActingAs)
        )

        setIsLoggedIn(!!sessionData?.permissions)

        return sessionData
      } catch (error) {
        resetAuthContextToDefault()
      }
    })()
    fetchSessionInFlight.current = promise
    try {
      return await promise
    } finally {
      fetchSessionInFlight.current = null
    }
  }

  /**
   * On mount, call GET /session to populate auth state before any route renders.
   * Without this, isLoggedIn defaults to false on every page load, causing
   * PrivateRoute to bounce to /login and show the login form skeleton during the
   * session round-trip. That is the visible flash on every account route and tab.
   *
   * Skipped in tests via the testConfig shortCircuit and when
   * NODE_ENV is test (covers tests that render AuthProvider directly
   * without passing test props). In those tests the spies control state
   * and the loading gate would only delay rendering by one tick.
   */
  useMountEffect(() => {
    if (testConfig || testConfig1 || process.env.NODE_ENV === 'test') {
      setIsInitialized(true)
      return
    }
    let cancelled = false
    const init = async () => {
      try {
        await fetchSession()
      } catch {
        // fetchSession own catch already reset state, nothing to do here.
      } finally {
        if (!cancelled) setIsInitialized(true)
      }
    }
    init()
    return () => {
      cancelled = true
    }
  })

  /**
   * Add loading context for signout so we can use a spinner on the Invite flow.
   * We could probably do this for other areas but let's start small.
   */
  useLoadingContext({
    asyncFunction: async () => {
      await logout()
    },
    loadingId: logoutLoadingId,
  })

  const resetAuthContextToDefault = () => {
    setPermissions([])
    setDefaults([])
    setUserDetails({
      userKey: 0,
      actingAs: 0,
    })
    setUsername('')
    setDiscourseUrl([])
    setCountryInformation({
      countryOfResidence: '',
      countryOfCitizenship: '',
    })
    setFeatureAbility(buildFeatureAbility([]))
    setPermissionAbility(buildPermissionAbility([]))
    setCanViewDashboard(false)
    setDefaultAccountPath(getLandingPath(false, 0))
    setIsLoggedIn(false)
  }

  const resetContext = () => {
    resetLearningCenterContextToDefaults?.()
    resetBusinessContextToDefaults?.()
    resetCommunitiesContextToDefaults?.()
    resetFiltersContextToDefaults?.()
  }
  return {
    isLoggedIn: !!isLoggedIn,
    permissions: permissions,
    permissionAbility,
    featureAbility,
    username,
    userDetails,
    discourseUrl,
    countryInformation,

    signin: async (credentials: {
      email: string
      password: string
    }): Promise<SigninResult | undefined> => {
      const isLoggedIn = await login(credentials)
      if (!isLoggedIn) return undefined
      const session = await fetchSession()
      if (!session) return undefined
      const permissionsAbility = buildPermissionAbility(
        session.permissions ?? []
      )
      const featureDefaultsAbility = buildFeatureAbility(
        session.featureDefaults ?? []
      )
      const userCanViewDashboard = hasDashboardPermission(
        permissionsAbility,
        featureDefaultsAbility
      )
      return {
        session,
        landingPath: getLandingPath(
          userCanViewDashboard,
          session.actingAs ?? 'parent'
        ),
      }
    },

    signup: async (payload: SignUpConfirmTokenRequestBody) => {
      const isSignUp = await confirmTokenForSignup(payload)
      if (isSignUp) {
        await fetchSession()
      }
      return isSignUp
    },
    updateActingAs: async (
      actorKey?: number
    ): Promise<UpdateActingAsResult> => {
      const success = await updateLogin(actorKey)
      await fetchSession()
      resetContext()
      return { success, actingAs: actorKey ?? ACTING_AS_PARENT }
    },
    signinForInvite: async (
      credentials: { username: string; password: string },
      signUpType: LoginForInviteRequestBodySignUpTypeEnum,
      typeIdentifier: string
    ): Promise<boolean> => {
      try {
        const isLoggedInForInvite = await loginForInvite(
          credentials,
          signUpType,
          typeIdentifier
        )
        if (isLoggedInForInvite) {
          await fetchSession()
        }
        return isLoggedInForInvite
      } catch (error) {
        await handleError(
          error,
          t(
            'AuthContext.Error.SigninForInvite',
            'An unknown error occurred during invite login.'
          )
        )
        return false
      }
    },
    async signout() {
      await logout()
      resetContext()
      resetAuthContextToDefault()
    },
    updateCountryInformation: (countryInformation) => {
      setCountryInformation(countryInformation)
    },
    revalidateSession: async (): Promise<FetchSessionResponse | undefined> => {
      return await fetchSession()
    },
    isInitialized,
    canViewDashboard,
    defaultAccountPath,
  }
}

interface AuthProviderProps extends PropsWithChildren {
  testConfig?: TestAuthConfig
  testConfig1?: TestAuthConfig1
}

const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({
  children,
  testConfig,
  testConfig1,
}) => {
  const { resetContextToDefaults: resetLearningCenterContextToDefaults } =
    useLearningCenterContext()
  const { resetContextToDefaults: resetBusinessContextToDefaults } =
    useBusinessContext()
  const { resetContextToDefaults: resetCommunitiesContextToDefaults } =
    useCommunitiesContext()
  const { resetContextToDefaults: resetFiltersContextToDefaults } =
    useFilterContext()
  const auth = useAuthContextValue(
    testConfig,
    testConfig1,
    resetLearningCenterContextToDefaults,
    resetBusinessContextToDefaults,
    resetCommunitiesContextToDefaults,
    resetFiltersContextToDefaults
  )

  /* Block route rendering until the initial GET /session resolves.
     Renders LoginSkeleton on /login (matches the login form dimensions)
     and AuthSkeleton elsewhere (matches the app layout dimensions).
     Uses window.location since AuthProvider sits above the router outlet.
     Skipped in tests via testConfig short circuit or when
     NODE_ENV is test (covers tests rendering AuthProvider directly). */
  if (
    !testConfig &&
    !testConfig1 &&
    process.env.NODE_ENV !== 'test' &&
    !auth.isInitialized
  ) {
    return window.location.pathname.startsWith('/login') ? (
      <LoginSkeleton />
    ) : (
      <AuthSkeleton />
    )
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

export default AuthProvider
